import { useCallback, useEffect } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
} from '@mui/material';
import { Formik, Field } from 'formik';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { FormikTextField } from '../../components/FormikFields';
import { landingPagesService } from '../../services/landing-pages.service';
import DraggableDialog from '../../components/DraggableDialog';
import TagSelect from '../../components/TagSelect';
import { CopyId } from '../../components/CopyId';
import { HistoryButton } from '../../components/EntityHistory';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import FormDirtyTracker from '../../components/FormDirtyTracker';

const schema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  url: Yup.string().trim().required('URL is required'),
  tag_ids: Yup.array().of(Yup.string()).default([]),
});

export default function CreateEditLandingPage() {
  const { modalState, toggleModal } = useModalState('landing-page');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['landing-page', modalState.id],
    queryFn: () => landingPagesService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  const isEdit = modalState.reason === 'Update' && !!entity;
  const isDuplicate = modalState.reason === 'Duplicate';
  const title = isEdit ? `Edit: ${entity?.name}` : isDuplicate ? 'Duplicate Landing Page' : 'Create Landing Page';

  const initialValues = (() => {
    if (!entity) return { name: '', notes: '', ref: '', url: '', tag_ids: [] as string[], workspace_id: defaultCreateWorkspace() };
    const base = { name: entity.name, notes: entity.notes ?? '', ref: isDuplicate ? '' : (entity.ref ?? ''), url: entity.url, tag_ids: entity.tag_ids ?? [], workspace_id: isDuplicate ? defaultCreateWorkspace() : (entity as any).workspace_id ?? defaultCreateWorkspace() };
    return base;
  })();

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="sm" fullWidth title={isLoading ? 'Loading...' : title} titleId="landing-page-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              setSubmitted();
              if (isEdit) {
                await landingPagesService.update(entity!.id, values);
              } else {
                await landingPagesService.create(values);
              }
              queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status, values, setFieldValue }: { submitForm: () => Promise<void>; isSubmitting: boolean; status?: string; values: { tag_ids: string[]; workspace_id: string }; setFieldValue: (f: string, v: unknown) => void }) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CopyId id={entity?.id} show={isEdit} />
                  <Box sx={{ flex: 1 }} />
                  {isEdit && <HistoryButton entityType="landing_pages" entityId={entity!.id} entityName={entity?.name} />}
                </Box>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <FormikTextField name="name" label="Name" size="small" sx={{ flex: 1 }} />
                    <FormikTextField name="ref" label="Ref" size="small" placeholder="e.g. L1200" sx={{ width: { xs: '100%', sm: 160 } }} />
                  </Box>
                  <FormikTextField
                    name="url"
                    label="Landing Page URL"
                    fullWidth
                    size="small"
                    placeholder="https://yourdomain.com/landing"
                  />
                  <FormikTextField name="notes" label="Notes" fullWidth size="small" multiline rows={2} />
                  {!isEdit && (
                    <Field name="workspace_id">
                      {({ field, form }: any) => (
                        <WorkspaceField value={field.value} onChange={(id) => form.setFieldValue('workspace_id', id)} />
                      )}
                    </Field>
                  )}
                  <TagSelect value={values.tag_ids} onChange={(ids) => setFieldValue('tag_ids', ids)} />
                  {status && <Alert severity="error">{status}</Alert>}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={guardedClose}>Cancel</Button>
                <Button variant="contained" onClick={submitForm} disabled={isSubmitting || !values.workspace_id}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}
    </DraggableDialog>
  );
}
